import api from './api';
import { logger } from '../utils/logger';

// ===== CONSTANTS =====

const ERROR_FAILED_TO_RECORD_SHARE = 'Failed to record share:';

// ===== TYPES =====

export interface Comment {
  commentId: string;
  streamId: string;
  userId: string;
  username: string;
  content: string;
  createdAt: number;
  updatedAt: number;
  likes: number;
  parentId?: string;
  timestampMs?: number; // Optional: timestamp in milliseconds for waveform comments
}

export interface CreateCommentInput {
  content: string;
  parentId?: string;
  timestampMs?: number; // Optional: timestamp in milliseconds for waveform comments
}

// ===== WAVEFORM COMMENT MARKERS =====

/**
 * A marker represents a cluster of comments at or near the same timestamp.
 * Comments within 3 seconds are grouped into a single marker to avoid cluttering the waveform.
 */
export interface CommentMarker {
  id: string; // Unique identifier for the marker (first comment's ID)
  timestampMs: number; // Position in milliseconds
  comments: Comment[]; // All comments clustered at this position
  commentCount: number; // Number of comments in this cluster
}

/**
 * Groups timestamped comments into markers by clustering nearby comments.
 * Comments within CLUSTER_WINDOW_MS (3 seconds) are grouped together.
 *
 * @param comments - Array of comments (will filter to only timestamped ones)
 * @param clusterWindowMs - Window in ms to cluster comments (default: 3000ms = 3 seconds)
 * @returns Array of CommentMarkers sorted by timestamp
 */
export const groupCommentsIntoMarkers = (
  comments: Comment[],
  clusterWindowMs: number = 3000
): CommentMarker[] => {
  // Filter to only timestamped comments and sort by timestamp
  const timestampedComments = comments
    .filter((c): c is Comment & { timestampMs: number } => c.timestampMs !== undefined)
    .sort((a, b) => a.timestampMs - b.timestampMs);

  if (timestampedComments.length === 0) {
    return [];
  }

  const markers: CommentMarker[] = [];
  let currentCluster: (Comment & { timestampMs: number })[] = [timestampedComments[0]];
  let clusterStartTime = timestampedComments[0].timestampMs;

  for (let i = 1; i < timestampedComments.length; i++) {
    const comment = timestampedComments[i];

    // If this comment is within the cluster window, add to current cluster
    if (comment.timestampMs - clusterStartTime <= clusterWindowMs) {
      currentCluster.push(comment);
    } else {
      // Create marker for completed cluster and start new one
      markers.push({
        id: currentCluster[0].commentId,
        timestampMs: clusterStartTime,
        comments: currentCluster,
        commentCount: currentCluster.length,
      });
      currentCluster = [comment];
      clusterStartTime = comment.timestampMs;
    }
  }

  // Don't forget the last cluster
  markers.push({
    id: currentCluster[0].commentId,
    timestampMs: clusterStartTime,
    comments: currentCluster,
    commentCount: currentCluster.length,
  });

  return markers;
};

export interface Rating {
  ratingId: string;
  streamId: string;
  userId: string;
  username: string;
  score: number;
  createdAt: number;
  updatedAt: number;
}

export interface CreateRatingInput {
  score: number;
}

export interface StreamRating {
  streamId: string;
  averageRating: number;
  totalRatings: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

export interface Share {
  shareId: string;
  streamId: string;
  userId: string;
  username: string;
  platform: 'facebook' | 'twitter' | 'instagram' | 'snapchat' | 'email' | 'copy';
  createdAt: number;
}

export interface CreateShareInput {
  platform: 'facebook' | 'twitter' | 'instagram' | 'snapchat' | 'email' | 'copy';
}

export interface StreamShareStats {
  streamId: string;
  totalShares: number;
  sharesByPlatform: {
    facebook: number;
    twitter: number;
    instagram: number;
    snapchat: number;
    email: number;
    copy: number;
  };
}

export interface Favorite {
  favoriteId: string;
  streamId: string;
  userId: string;
  username: string;
  createdAt: number;
}

export interface FavoriteWithStream extends Favorite {
  stream?: {
    streamId: string;
    name: string;
    description?: string;
    coverImage?: string;
    audioUrl?: string;
    uploaderUsername?: string;
    uploaderDisplayName?: string;
  };
}

export interface UserFavoritesResponse {
  favorites: FavoriteWithStream[];
  nextToken?: string;
}

export interface StreamFavoriteStats {
  streamId: string;
  totalFavorites: number;
  favoritedBy: Array<{
    userId: string;
    username: string;
    favoritedAt: number;
  }>;
}

export interface Play {
  playId: string;
  streamId: string;
  userId?: string;
  username?: string;
  playedAt: number;
  duration?: number;
}

export interface StreamPlayStats {
  streamId: string;
  totalPlays: number;
  uniqueUsers: number;
  playedBy: Array<{
    userId: string;
    username: string;
    lastPlayedAt: number;
    playCount: number;
  }>;
}

// ===== COMMENTS =====

const getComments = async (streamId: string): Promise<Comment[]> => {
  return await api.get<Comment[]>(`/streams/${streamId}/comments`);
};

const createComment = async (streamId: string, input: CreateCommentInput): Promise<Comment> => {
  return await api.post<Comment>(`/streams/${streamId}/comments`, input);
};

const updateComment = async (commentId: string, content: string): Promise<Comment> => {
  return await api.put<Comment>(`/comments/${commentId}`, { content });
};

const deleteComment = async (commentId: string): Promise<void> => {
  await api.delete<void>(`/comments/${commentId}`);
};

const likeComment = async (commentId: string): Promise<void> => {
  await api.post<void>(`/comments/${commentId}/like`);
};

// ===== RATINGS =====

const getRatings = async (streamId: string): Promise<Rating[]> => {
  return await api.get<Rating[]>(`/streams/${streamId}/ratings`);
};

const getRatingSummary = async (streamId: string): Promise<StreamRating> => {
  return await api.get<StreamRating>(`/streams/${streamId}/rating-summary`);
};

const getUserRating = async (streamId: string): Promise<Rating | null> => {
  try {
    return await api.get<Rating>(`/streams/${streamId}/ratings/me`);
  } catch (error: any) {
    if (error.response?.status === 401) {
      return null; // User not authenticated
    }
    throw error;
  }
};

const createOrUpdateRating = async (streamId: string, input: CreateRatingInput): Promise<Rating> => {
  return await api.post<Rating>(`/streams/${streamId}/ratings`, input);
};

const deleteRating = async (streamId: string): Promise<void> => {
  await api.delete<void>(`/streams/${streamId}/ratings`);
};

// ===== SHARES =====

const getShareStats = async (streamId: string): Promise<StreamShareStats> => {
  return await api.get<StreamShareStats>(`/streams/${streamId}/share-stats`);
};

const createShare = async (streamId: string, input: CreateShareInput): Promise<Share> => {
  return await api.post<Share>(`/streams/${streamId}/shares`, input);
};

// `/streams/{id}` is the route App.tsx renders StreamDetail on. The old
// `/stream/{id}` fell through to the catch-all and opened Home (TEM-1631).
const getShareUrl = (streamId: string): string => {
  return `${window.location.origin}/streams/${encodeURIComponent(streamId)}`;
};

const shareToFacebook = async (streamId: string): Promise<void> => {
  const url = getShareUrl(streamId);
  // Try to record share, but don't fail if user is not authenticated
  try {
    await createShare(streamId, { platform: 'facebook' });
  } catch (error: any) {
    if (error.response?.status !== 401) {
      logger.error(ERROR_FAILED_TO_RECORD_SHARE, error);
    }
  }
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    '_blank',
    'width=600,height=400'
  );
};

const shareToTwitter = async (streamId: string, text: string): Promise<void> => {
  const url = getShareUrl(streamId);
  // Try to record share, but don't fail if user is not authenticated
  try {
    await createShare(streamId, { platform: 'twitter' });
  } catch (error: any) {
    if (error.response?.status !== 401) {
      logger.error(ERROR_FAILED_TO_RECORD_SHARE, error);
    }
  }
  window.open(
    `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    '_blank',
    'width=600,height=400'
  );
};

const shareToInstagram = async (streamId: string): Promise<void> => {
  const url = getShareUrl(streamId);
  // Try to record share, but don't fail if user is not authenticated
  try {
    await createShare(streamId, { platform: 'instagram' });
  } catch (error: any) {
    if (error.response?.status !== 401) {
      logger.error(ERROR_FAILED_TO_RECORD_SHARE, error);
    }
  }
  // Instagram doesn't have a direct share URL, so copy link and notify user
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(url);
  }
  alert('Link copied! You can now paste it in Instagram.');
};

const shareToSnapchat = async (streamId: string): Promise<void> => {
  const url = getShareUrl(streamId);
  // Try to record share, but don't fail if user is not authenticated
  try {
    await createShare(streamId, { platform: 'snapchat' });
  } catch (error: any) {
    if (error.response?.status !== 401) {
      logger.error(ERROR_FAILED_TO_RECORD_SHARE, error);
    }
  }
  // Snapchat share URL for web
  window.open(
    `https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent(url)}`,
    '_blank',
    'width=600,height=400'
  );
};

const shareViaEmail = async (streamId: string, subject: string, body: string): Promise<void> => {
  const url = getShareUrl(streamId);
  // Try to record share, but don't fail if user is not authenticated
  try {
    await createShare(streamId, { platform: 'email' });
  } catch (error: any) {
    if (error.response?.status !== 401) {
      logger.error(ERROR_FAILED_TO_RECORD_SHARE, error);
    }
  }
  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body + '\n\n' + url)}`;
};

const copyShareLink = async (streamId: string): Promise<string> => {
  const url = getShareUrl(streamId);
  // Try to record share, but don't fail if user is not authenticated
  try {
    await createShare(streamId, { platform: 'copy' });
  } catch (error: any) {
    if (error.response?.status !== 401) {
      logger.error(ERROR_FAILED_TO_RECORD_SHARE, error);
    }
  }

  if (navigator.clipboard) {
    await navigator.clipboard.writeText(url);
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }

  return url;
};

// ===== FAVORITES =====

const getFavoriteStats = async (streamId: string): Promise<StreamFavoriteStats> => {
  return await api.get<StreamFavoriteStats>(`/streams/${streamId}/favorite-stats`);
};

const getUserFavorite = async (streamId: string): Promise<Favorite | null> => {
  try {
    return await api.get<Favorite>(`/streams/${streamId}/favorites/me`);
  } catch (error: any) {
    if (error.response?.status === 401) {
      return null; // User not authenticated
    }
    throw error;
  }
};

const addFavorite = async (streamId: string): Promise<Favorite> => {
  return await api.post<Favorite>(`/streams/${streamId}/favorites`, {});
};

const removeFavorite = async (streamId: string): Promise<void> => {
  await api.delete<void>(`/streams/${streamId}/favorites`);
};

const getUserFavorites = async (limit: number = 20, nextToken?: string): Promise<UserFavoritesResponse> => {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (nextToken) {
    params.append('nextToken', nextToken);
  }
  return await api.get<UserFavoritesResponse>(`/user/favorites?${params.toString()}`);
};

// ===== PLAYS =====

const getPlayStats = async (streamId: string): Promise<StreamPlayStats> => {
  return await api.get<StreamPlayStats>(`/streams/${streamId}/play-stats`);
};

const recordPlay = async (streamId: string, duration?: number): Promise<Play> => {
  try {
    return await api.post<Play>(`/streams/${streamId}/plays`, { duration });
  } catch (error: any) {
    // Don't fail silently for authenticated users - log the error
    if (error.response?.status !== 401) {
      logger.error('Failed to record play', error);
    }
    throw error;
  }
};

const socialService = {
  // Comments
  getComments,
  createComment,
  updateComment,
  deleteComment,
  likeComment,
  groupCommentsIntoMarkers,

  // Ratings
  getRatings,
  getRatingSummary,
  getUserRating,
  createOrUpdateRating,
  deleteRating,

  // Shares
  getShareStats,
  createShare,
  getShareUrl,
  shareToFacebook,
  shareToTwitter,
  shareToInstagram,
  shareToSnapchat,
  shareViaEmail,
  copyShareLink,

  // Favorites
  getFavoriteStats,
  getUserFavorite,
  getUserFavorites,
  addFavorite,
  removeFavorite,

  // Plays
  getPlayStats,
  recordPlay,
};

export default socialService;
